/**
 * Auth failures the form already shows.
 *
 * Sign-up, OTP, and password update throw these when Supabase returns
 * `response.error`, or when sign-up finds an email that already has an
 * account. They must not reject the react-hook-form submit promise. That
 * handler's rejection is otherwise an unhandled browser rejection, and
 * Sentry records it as a crash.
 *
 * A string counts because the auth hooks in these submit handlers throw
 * `response.error.message` directly, and that is the only string they throw.
 * A Supabase AuthError sets `__isAuthError`. Weak passwords use our own
 * error name, and a taken email uses a fixed message.
 */
const USER_ALREADY_REGISTERED_MESSAGE = 'User already registered';

export function isUserAlreadyRegisteredError(error: unknown): boolean {
  return readErrorMessage(error) === USER_ALREADY_REGISTERED_MESSAGE;
}

export function isExpectedAuthFailure(error: unknown): boolean {
  if (typeof error === 'string') {
    return error.length > 0;
  }

  if (!isRecord(error)) {
    return false;
  }

  if (error.__isAuthError === true) {
    return true;
  }

  // Local WeakPasswordError does not set __isAuthError, so the check above
  // misses it.
  if (error instanceof Error && error.name === 'WeakPasswordError') {
    return true;
  }

  return isUserAlreadyRegisteredError(error);
}

/**
 * Rethrows anything that is not an expected auth failure.
 *
 * Callers must return after this. An expected failure returns normally so
 * the mutation error state can render, and falling through would treat it
 * as success.
 */
export function rethrowUnexpectedAuthFailure(error: unknown): void {
  if (!isExpectedAuthFailure(error)) {
    throw error;
  }
}

function readErrorMessage(error: unknown): string | undefined {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
